import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Col, Form, Image, Row } from "react-bootstrap";
import { calculator } from "../utils/Calculator";
import { SortedArrowIcon } from "../data/iconImage";
import {
  filteredItemInArray,
  hasDuplicateValues,
  hasValueObjects
} from "../utils/ManageArray";
import {
  autoDisplayValue,
  formatSplitKey,
  plusStringValue
} from "../commons/FormatValue";
import {
  FloatingControlComponent,
  FormCheckComponent,
  FormControlComponent,
  SearchSelectComponent
} from "./FormComponent";
import ButtonOptionGroup from "./ButtonOptionGroup";
import BarcodeGenerator from "./BarcodeGenerator";

const TableForm = ({
  data,
  columns,
  defaultData,
  editForm,
  duplicateKey,
  passKey,
  checkBoxShow = false,
  linkBtn,
  activeButton = ["add", "copy", "delete"],
  onUploadCallback,
  onChangeCallback,
  navView
}) => {
  const [newData, setNewData] = useState([]);
  const [sortBy, setSortBy] = useState("rowIndex");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [displayValue, setDisplayValue] = useState({
    findValue: null,
    options: null
  });

  const handleSort = (name) => {
    const nextOrder =
      sortBy === name ? (sortOrder === "asc" ? "desc" : "asc") : "asc";

    const sortFunction = (A, B) => {
      if (sortOrder === "asc") return A > B ? 1 : -1;
      else return A < B ? 1 : -1;
    };

    const sortedData = sortBy
      ? [...newData].sort((a, b) => sortFunction(a?.[name], b?.[name]))
      : newData;

    const sortedDisplay = sortBy
      ? [...displayValue].map((row) => {
          const dataIndex = row.findIndex((cell) => cell.name === name);
          if (dataIndex !== -1) {
            return row.sort((a, b) =>
              sortFunction(
                a?.data?.[dataIndex]?.[name],
                b?.data?.[dataIndex]?.[name]
              )
            );
          }
          return row;
        })
      : displayValue;

    setSortBy(name);
    setSortOrder(nextOrder);
    setNewData(sortedData);
    setDisplayValue(sortedDisplay);
    onChangeCallback(sortedData, sortedDisplay);
  };

  const handleDisplay = (name, value, options) => {
    columns.forEach(({ findValue }) => {
      if (findValue?.value === name) {
        const { data, key } = findValue || {};
        const filteredFind = filteredItemInArray(data, key, value);
        const filteredOption = filteredItemInArray(options, "value", value);
        setDisplayValue((prev) => ({
          ...prev,
          findValue: {
            ...prev.findValue,
            [name]: prev.findValue?.[name]
              ? [...prev.findValue?.[name], ...filteredFind]
              : filteredFind
          },
          options: {
            ...prev.options,
            [name]: prev.options?.[name]
              ? [...prev.options?.[name], ...filteredOption]
              : filteredOption
          }
        }));
      }
    });
  };

  const handleChange = (index, name, value, options) => {
    const updatedData = [
      ...newData?.slice(0, index),
      { ...newData[index], [name]: value },
      ...newData?.slice(index + 1)
    ];

    handleDisplay(name, value, options);

    setNewData(updatedData);
    onChangeCallback(updatedData, displayValue);
  };

  const calculateValues = ({ row, sumValue }) => {
    const { firstName, secondName, operator } = sumValue;
    const num1 = Number(row?.[firstName]);
    const num2 = Number(row?.[secondName]);

    return calculator({ num1, num2, operator });
  };

  const handleCheckboxChange = (index, name, value) => {
    const updatedCheckboxState = [...newData];
    updatedCheckboxState[index] = {
      ...updatedCheckboxState[index],
      isChecked: value
    };

    const allChecked = updatedCheckboxState.every(({ isChecked }) => isChecked);
    const someChecked = updatedCheckboxState.some(({ isChecked }) => isChecked);

    setNewData(updatedCheckboxState);
    onChangeCallback(updatedCheckboxState);
    setSelectAllChecked(allChecked ? true : someChecked ? null : false);
  };

  const handleSelectAllChange = () => {
    const updatedCheckboxState = newData.map((row) => ({
      ...row,
      isChecked: !selectAllChecked
    }));

    setNewData(updatedCheckboxState);
    onChangeCallback(updatedCheckboxState);
    setSelectAllChecked(!selectAllChecked);
  };

  useEffect(() => {
    setNewData(data || []);

    const updatedPK = columns
      .filter(({ options }) => options)
      .map(
        ({ name }) =>
          Object.keys(defaultData).filter((def) => name === def)?.[0]
      );
    const { hasValue } = hasValueObjects(
      newData,
      passKey?.length > 0 ? passKey : updatedPK
    );

    if (hasValue?.length === 0) {
      for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < columns.length; j++) {
          const name = columns?.[j]?.name;
          const result = formatSplitKey(name, ".", data?.[i]);
          const checkValue = columns.some(
            ({ findValue }) => data?.[i]?.name === findValue?.value
          );

          if (!columns?.[j]?.findValue && result && checkValue) {
            handleDisplay(name, result, columns?.[j]?.options);
          }
        }
      }
    }
  }, [data, newData]);

  return (
    <Form.Group className=" TableForm" style={{ height: "max-content" }}>
      <Row className="mt-1 mx-0 px-0 border-bottom">
        {checkBoxShow && (
          <Col
            xs={1}
            className="d-flex justify-content-center align-items-center"
            style={{ width: "50px" }}
          >
            <FormCheckComponent
              type="checkbox"
              className={{ input: "ms-3 mb-2" }}
              onChange={handleSelectAllChange}
              checked={selectAllChecked}
            />
          </Col>
        )}

        <Col xs={1} className="ps-0 pe-3" style={{ width: "75px" }}>
          <Form.Label
            className="d-flex fs-3 fw-bold"
            onClick={() => handleSort("rowIndex", 0)}
          >
            ลำดับ
            {sortBy === "rowIndex" && (
              <Image
                src={SortedArrowIcon}
                alt="Sorted Icon"
                style={{ rotate: sortOrder === "asc" ? "180deg" : "0deg" }}
              />
            )}
          </Form.Label>
        </Col>

        {columns.map(({ name, label, size }, idx) => (
          <Col key={idx} sm={size || null} className="px-3">
            <Form.Label
              className="d-flex fs-3 fw-bold"
              onClick={() => handleSort(name, idx)}
            >
              {label}
              {sortBy === name && (
                <Image
                  src={SortedArrowIcon}
                  alt="Sorted Icon"
                  style={{ rotate: sortOrder === "asc" ? "180deg" : "0deg" }}
                />
              )}
            </Form.Label>
          </Col>
        ))}

        {!editForm && (
          <Row
            style={{
              width:
                linkBtn?.length > 0
                  ? `${linkBtn?.length * 40 + 70}px`
                  : activeButton?.length > 0
                  ? `${activeButton?.length * 40 + 70}px`
                  : "150px"
            }}
          ></Row>
        )}
      </Row>

      {newData.length > 0 ? (
        newData?.map((row, index) => (
          <Row key={index} className="mt-1 mx-0 border-bottom">
            {checkBoxShow && (
              <Col
                xs={1}
                className="d-flex justify-content-center align-items-center"
                style={{ width: "50px" }}
              >
                <FormCheckComponent
                  type="checkbox"
                  name="isChecked"
                  className={{ input: "ms-3" }}
                  onChange={() =>
                    handleCheckboxChange(index, "isChecked", !row?.isChecked)
                  }
                  checked={row?.isChecked || false}
                />
              </Col>
            )}

            <Col xs={1} className="ps-0 pe-3" style={{ width: "75px" }}>
              <FormControlComponent
                readOnly
                type="text"
                className={{ input: "border-0" }}
                value={index + 1}
              />
            </Col>

            {columns.map(
              (
                {
                  name,
                  label,
                  type,
                  size,
                  min,
                  max,
                  options,
                  format,
                  filterBy,
                  sumValue,
                  findValue,
                  plusString,
                  readOnly,
                  disabled,
                  pattern,
                  feedbackMessage,
                  noneRequired,
                  minKeywordLength,
                  queryService,
                  noneAsync,
                  textColor
                },
                idx
              ) => {
                const colVal = formatSplitKey(name, ".", row);
                const arrFindVal =
                  displayValue?.findValue?.[findValue?.value || name];
                const arrOptions =
                  displayValue?.options?.[findValue?.value || name];
                const findRowVal = row?.[findValue?.value];
                const filterVal = filteredItemInArray(
                  arrFindVal,
                  findValue?.key,
                  findRowVal
                );
                const filterOp = filteredItemInArray(
                  arrOptions,
                  "value",
                  colVal
                );
                const option = filterOp?.length > 0 ? filterOp : options;
                const findedVal = autoDisplayValue({
                  name,
                  type,
                  findValue: {
                    ...findValue,
                    data: filterVal,
                    value: findRowVal
                  }
                });
                const result =
                  (format ? format(row) : "") ||
                  (sumValue && calculateValues({ row, sumValue })) ||
                  (plusString && plusStringValue(plusString, "  ", row)) ||
                  (findValue && findedVal) ||
                  (colVal === false ? false : colVal === 0 ? "0" : colVal);
                const validDupilcate =
                  duplicateKey?.some((dup) => dup === name) &&
                  hasDuplicateValues(newData, duplicateKey);

                return (
                  <Col key={idx} sm={size || null} className="ps-0 pe-3">
                    {type === "select" ? (
                      <SearchSelectComponent
                        labelField={name}
                        filterBy={filterBy}
                        disabled={disabled}
                        noneAsync={noneAsync}
                        placeholder="โปรดเลือก"
                        minKeywordLength={minKeywordLength}
                        options={option}
                        queryService={queryService}
                        isInvalid={
                          (!noneRequired && ["", null].includes(result)) ||
                          result === false ||
                          validDupilcate
                        }
                        onChange={(change) =>
                          handleChange(index, name, change?.value, option)
                        }
                        value={result}
                      />
                    ) : type === "barcode" ? (
                      result && <BarcodeGenerator value={`${result}`} />
                    ) : (
                      <FloatingControlComponent
                        type={type}
                        name={name}
                        label={label}
                        pattern={pattern}
                        readOnly={readOnly}
                        disabled={disabled}
                        min={row?.[min] || `${min}`}
                        max={row?.[max] || `${max}`}
                        placeholder="โปรดระบุ"
                        className={{ input: `border-0 ${textColor}` }}
                        feedbackMessage={feedbackMessage}
                        required={
                          (!noneRequired && result === "") || validDupilcate
                        }
                        onChange={({ target }) =>
                          handleChange(index, name, target?.value.toUpperCase())
                        }
                        value={result}
                      />
                    )}
                  </Col>
                );
              }
            )}

            {!editForm && (
              <ButtonOptionGroup
                data={newData}
                rowIndex={index}
                linkBtn={linkBtn}
                navView={{ ...navView, state: row }}
                defaultData={defaultData}
                activeButton={activeButton}
                onUploadCallback={(file) => onUploadCallback(file, index)}
                onAddRowCallback={(add) => onChangeCallback(add, displayValue)}
                onCopiedRowCallback={(copy) =>
                  onChangeCallback(copy, displayValue)
                }
                onDeleteRowCallback={(del) =>
                  onChangeCallback(del, displayValue)
                }
              />
            )}
          </Row>
        ))
      ) : (
        <Row className="mt-1 mx-0 px-0 justify-content-start border-bottom">
          <Col xs={12} className="py-3 fs-4 text-center text-secondary">
            ไม่พบข้อมูล
          </Col>
        </Row>
      )}
    </Form.Group>
  );
};

TableForm.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  defaultData: PropTypes.object,
  editForm: PropTypes.bool,
  duplicateKey: PropTypes.array,
  passKey: PropTypes.array,
  checkBoxShow: PropTypes.bool,
  linkBtn: PropTypes.array,
  activeButton: PropTypes.array,
  onUploadCallback: PropTypes.func,
  onChangeCallback: PropTypes.func,
  navView: PropTypes.object
};

export default TableForm;
